/**
 * Subscription guard middleware that validates org-level subscription status.
 * 
 * This middleware:
 * - Allows public routes without checks
 * - Ensures org data is loaded before checking subscription
 * - FREE tier users can view all pages (pages show upgrade prompts)
 * - Validates trial and subscription end dates
 */
export default defineNuxtRouteMiddleware(async (to) => {
  if (import.meta.server) {
    return
  }

  if (import.meta.dev && to.meta.publicHelpPreview === true) {
    return
  }

  const userStore = useUserStore()
  const orgStore = useOrgStore()
  const { user } = userStore

  const publicRoutes = [
    '/',
    '/login',
    '/signup',
    '/forgotPassword',
    '/reset-password',
    '/pricing',
    '/contact',
    '/verify-email',
    '/signup-complete',
    '/accept-terms',
    '/logout',
    '/organizationSettings',
    '/copilot'
  ]

  if (publicRoutes.includes(to.path) || to.path.startsWith('/signup/') || to.path.startsWith('/copilot/')) {
    return
  }

  // Terms acceptance is a higher-priority authentication gate. Do not let a
  // simultaneous org/subscription lookup redirect the user elsewhere and
  // accidentally override authGuard's /accept-terms navigation.
  if (useAuth().requiresTermsAcceptance.value) {
    return navigateTo('/accept-terms')
  }

  if (!user) {
    return
  }

  let org = orgStore.selected

  if (!org || !org.id) {
    try {
      await orgStore.getOrg()
    } catch (error) {
      console.error('Failed to load org data:', error)
      return navigateTo('/organizationSettings?tab=administration')
    }
  }

  org = orgStore.selected
  if (!org || !org.id) {
    return navigateTo('/organizationSettings?tab=administration')
  }

  if (org.subscriptionTier === 'free') {
    return
  }

  if (!hasValidOrgSubscription(org)) {
    return navigateTo('/organizationSettings?tab=administration')
  }
})

/**
 * Validates org-level subscription status.
 * @param org - The organization object with subscription data
 * @returns true if the org has a valid subscription
 */
function hasValidOrgSubscription(org: any): boolean {
  // Prefer the backend-computed decision (GET /org attaches `access`,
  // evaluated by the exact rules the API enforces). The local re-derivation
  // below is only a fallback for stale cached org objects — keeping two
  // implementations in sync by hand is how pending_cancellation users got
  // locked out.
  if (org.access && typeof org.access.platform === 'boolean') {
    return org.access.platform
  }

  const tier = org.subscriptionTier
  const status = org.subscriptionStatus

  if (tier === 'free') {
    return true
  }

  if (status === 'active' || status === 'trialing') {
    return true
  }

  if (tier === 'trial' && org.trialEndsAt) {
    const trialEnd = new Date(org.trialEndsAt)
    return new Date() < trialEnd
  }

  // Scheduled cancellation: the customer already paid for the current period
  // and keeps full access until subscription_ends_at — mirrors the backend's
  // hasPaidAccess (subscription_middleware.go), including its fail-open when
  // no end date is recorded.
  if (status === 'pending_cancellation') {
    if (org.subscriptionEndsAt) {
      return new Date() < new Date(org.subscriptionEndsAt)
    }
    return true
  }

  if (status === 'past_due' && org.subscriptionEndsAt) {
    const gracePeriodDays = 7
    const endDate = new Date(org.subscriptionEndsAt)
    const graceEnd = new Date(endDate.getTime() + gracePeriodDays * 24 * 60 * 60 * 1000)
    return new Date() < graceEnd
  }

  if (status === 'canceled' && org.subscriptionEndsAt) {
    const endDate = new Date(org.subscriptionEndsAt)
    return new Date() < endDate
  }

  return false
}
